import React, { useState, useEffect, useCallback, useRef, useId } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useFetchWrapper } from '../../_helpers/fetchWrapper.js';
import styles from '../../styles/partial/PerformerSongList.module.css';

import config from '../../config.js';
import { resolveMediaUrl } from '../../_helpers/mediaUrl.js';
import Switch from '../Switch.jsx';
import EditSongModal from '../partial/modal/EditSongModal.jsx';
import ConfirmationModal from './modal/ConfirmationModal.jsx';
import { serverMessage } from '../../_helpers/errorMessage.js';
import { capturePerformerEvent } from '../../_helpers/performerEvents.js';
import SongFilter from './SongFilter.jsx';
import ActionMenuButton from './ActionMenuButton.jsx';
import { FaEdit, FaTrash, FaSortUp, FaSortDown, FaSort, FaInfoCircle, FaPlus } from 'react-icons/fa';
import { useSongSearch } from '../../contexts/SongSearchContext.jsx';
import { useLocale } from '../../contexts/LocaleContext.jsx';

const PerformerSongList = ({ resetTagFilterRef }) => {
  const { bandid } = useParams();
  const { searchTerm, performSearch } = useSongSearch();
  const { t, format } = useLocale();
  const [songs, setSongs] = useState([]);
  const [songsFetched, setSongsFetched] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [highlightedRow, setHighlightedRow] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [currentSong, setCurrentSong] = useState(null);
  const [bandTags, setBandTags] = useState([]);
  const [selectedTagFilter, setSelectedTagFilter] = useState(null);
  const [selectedSongIds, setSelectedSongIds] = useState(new Set());
  const [visibilityFilter, setVisibilityFilter] = useState('all');
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const selectionCountId = useId();
  // A failed toggle or bulk action, shown above the list (ENG-507):
  // { key, params } of a catalog message.
  const [listError, setListError] = useState(null);
  // The song awaiting a Delete confirmation, and why the last delete failed.
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchWrapperRef = useRef(useFetchWrapper());

  useEffect(() => {
    if (resetTagFilterRef) {
      resetTagFilterRef.current = () => {
        setSelectedTagFilter(null);
      };
    }
  }, [resetTagFilterRef]);

  const fetchSongsData = useCallback(async () => {
    try {
      const [songsResponse, tagsResponse] = await Promise.all([
        fetchWrapperRef.current.get(`${config.apiBaseUrl}/bands/${bandid}/songs`),
        fetchWrapperRef.current.get(`${config.apiBaseUrl}/bands/${bandid}/tags`).catch(() => ({ tags: [], success: true }))
      ]);

      if (songsResponse && songsResponse.success && Array.isArray(songsResponse.songs)) {
        setSongs(songsResponse.songs);
      } else if (Array.isArray(songsResponse)) {
        setSongs(songsResponse);
      } else {
        console.warn('Unexpected songs response format:', songsResponse);
        setSongs([]);
      }

      if (tagsResponse && tagsResponse.success && Array.isArray(tagsResponse.tags)) {
        setBandTags(tagsResponse.tags);
      } else if (Array.isArray(tagsResponse)) {
        setBandTags(tagsResponse);
      } else {
        setBandTags([]);
      }

      setSongsFetched(true);
    } catch (error) {
      console.error('Error fetching songs data:', error);
      setSongs([]);
      setBandTags([]);
      setSongsFetched(true);
    }
  }, [bandid, fetchWrapperRef]);

  useEffect(() => {
    if (songsFetched) return;
    fetchSongsData();
  }, [songsFetched, fetchSongsData]);

  const toggleActive = async (songId, currentStatus) => {
    setListError(null);
    try {
      await fetchWrapperRef.current.put(`${config.apiBaseUrl}/bands/${bandid}/songs/${songId}`, {
        active: !currentStatus,
      });
      setSongs((prevSongs) =>
        prevSongs.map((song) =>
          song.id === songId ? { ...song, active: !currentStatus } : song
        )
      );
    } catch (error) {
      console.error('Error updating song status:', error);
      const song = songs.find((s) => s.id === songId);
      setListError({ key: 'songList.errors.toggleFailed', params: { title: song?.title ?? '' } });
    }
  };

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const sortedSongs = React.useMemo(() => {
    let sortableSongs = [...songs];

    if (searchTerm && searchTerm.trim()) {
      const searchLower = searchTerm.trim().toLowerCase();
      sortableSongs = sortableSongs.filter(song =>
        (song.title || '').toLowerCase().includes(searchLower) ||
        (song.artist || '').toLowerCase().includes(searchLower)
      );
    }

    if (selectedTagFilter) {
      const filterTagId = selectedTagFilter.id ?? selectedTagFilter.ID;
      sortableSongs = sortableSongs.filter(song => {
        const songTags = song.tags || [];
        return songTags.some(tag => (tag.id ?? tag.ID) === filterTagId);
      });
    }

    if (visibilityFilter === 'public') {
      sortableSongs = sortableSongs.filter(s => s.active);
    } else if (visibilityFilter === 'hidden') {
      sortableSongs = sortableSongs.filter(s => !s.active);
    }

    if (sortConfig.key !== null) {
      sortableSongs.sort((a, b) => {
        let aValue, bValue;

        if (sortConfig.key === 'active') {
          aValue = a.active ? 1 : 0;
          bValue = b.active ? 1 : 0;
        } else if (sortConfig.key === 'id') {
          aValue = Number(a.id ?? a.ID);
          bValue = Number(b.id ?? b.ID);
        } else {
          aValue = a[sortConfig.key];
          bValue = b[sortConfig.key];
          if (sortConfig.key === 'title' || sortConfig.key === 'artist') {
            aValue = aValue ? String(aValue).trim().toLowerCase() : 'zzzzzzz';
            bValue = bValue ? String(bValue).trim().toLowerCase() : 'zzzzzzz';
          }
        }

        if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
      });
    }
    return sortableSongs;
  }, [songs, sortConfig, selectedTagFilter, searchTerm, visibilityFilter]);

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className={styles.sortIcon} aria-hidden="true" />;
    return sortConfig.direction === 'ascending'
      ? <FaSortUp className={styles.sortIconActive} aria-hidden="true" />
      : <FaSortDown className={styles.sortIconActive} aria-hidden="true" />;
  };

  const handleEdit = (song) => {
    setCurrentSong(song);
    setEditModalOpen(true);
    // A song added anyway has its genres and explicit flag only in the
    // single-song read (ENG-793/795), so the modal has every field the band
    // can edit.
    if (song.is_manual === true) {
      const songId = song.id ?? song.ID;
      fetchWrapperRef.current
        .get(`${config.apiBaseUrl}/bands/${bandid}/songs/${songId}`)
        .then((detail) => {
          if (!detail || typeof detail !== 'object') return;
          setCurrentSong((current) =>
            current && (current.id ?? current.ID) === songId ? { ...current, ...detail, tags: current.tags } : current
          );
        })
        .catch((error) => console.error('Error loading song details:', error));
    }
  };

  // Song list edits for PostHog (ENG-888), sent once the request succeeded:
  // the song's id and flags only, never its title or notes.
  const trackSongEvent = (name, songId, properties = {}) => {
    const id = Number(songId);
    void capturePerformerEvent(name, {
      bandId: bandid,
      fetchWrapper: fetchWrapperRef.current,
      ...(Number.isSafeInteger(id) && id > 0 && { song_id: id }),
      ...properties,
    });
  };

  // Delete asks first (ENG-507); it used to remove the song on the first
  // click. A failure keeps the confirmation open and says why.
  const handleDelete = (song) => {
    setDeleteError(null);
    setPendingDelete(song);
  };

  const confirmDelete = async () => {
    if (!pendingDelete || isDeleting) return;
    const songId = pendingDelete.id;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await fetchWrapperRef.current.delete(`${config.apiBaseUrl}/bands/${bandid}/songs/${songId}`);
      trackSongEvent('songlist_song_removed', songId);
      setSongs((prevSongs) => prevSongs.filter((song) => song.id !== songId));
      setSelectedSongIds(prev => {
        const next = new Set(prev);
        next.delete(songId);
        return next;
      });
      setPendingDelete(null);
    } catch (error) {
      console.error('Error deleting song:', error);
      setDeleteError(serverMessage(error) || t('songList.errors.deleteFailed'));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleMenuToggle = (songId) => {
    if (openMenuId === songId) {
      setOpenMenuId(null);
      setHighlightedRow(null);
    } else {
      setOpenMenuId(songId);
      setHighlightedRow(songId);
    }
  };

  const closeModal = () => {
    setEditModalOpen(false);
    setCurrentSong(null);
  };

  /**
   * Puts `saved` in the list in place of the song `songId` was. "This isn't
   * the right song" and "Find this song" each give the song a new id
   * (ENG-795/796), which the list, the selection and the open menu all
   * switch to, so later requests use it.
   */
  const replaceSong = (songId, saved) => {
    const savedId = saved.id ?? saved.ID;
    setSongs((prevSongs) =>
      prevSongs.map((song) => ((song.id ?? song.ID) === songId ? saved : song))
    );
    if (savedId !== songId) {
      setSelectedSongIds((prev) => {
        if (!prev.has(songId)) return prev;
        const next = new Set(prev);
        next.delete(songId);
        next.add(savedId);
        return next;
      });
      setOpenMenuId((current) => (current === songId ? null : current));
      setHighlightedRow((current) => (current === songId ? null : current));
    }
  };

  /** The song a request answered with, over what the band had; null when it sent none. */
  const songFrom = (response, before) => {
    const returned = response && typeof response === 'object' && !Array.isArray(response) && response.id != null;
    return returned ? { ...before, ...response, tags: response.tags ?? before.tags } : null;
  };

  // Rejects with the request's error so the modal can say why (ENG-794).
  const handleSave = async (updatedSong) => {
    const songId = updatedSong.id ?? updatedSong.ID;
    let response;
    try {
      response = await fetchWrapperRef.current.put(`${config.apiBaseUrl}/bands/${bandid}/songs/${songId}`, updatedSong);
    } catch (error) {
      console.error('Error saving song:', error);
      throw error;
    }
    trackSongEvent('song_edited', songId, { edit: 'details' });
    // Editing a manual song's song info answers with the song as the band
    // now has it (ENG-793/795).
    replaceSong(songId, songFrom(response, updatedSong) ?? updatedSong);
    closeModal();
  };

  // "This isn't the right song" (ENG-796): the band's own copy replaces the
  // catalogue song under a new id, and the modal stays open on the copy so
  // its song info can be filled in. Rejects with the request's error.
  const handleConvertToManual = async (song) => {
    const songId = song.id ?? song.ID;
    const response = await fetchWrapperRef.current.put(`${config.apiBaseUrl}/bands/${bandid}/songs/${songId}`, { manual: true });
    trackSongEvent('song_edited', songId, { edit: 'manual' });
    const copy = songFrom(response, song) ?? { ...song, is_manual: true };
    replaceSong(songId, copy);
    setCurrentSong(copy);
  };

  // "Find this song" (ENG-796): looks the song up again; nothing is written.
  const handleFindMatch = (song, query) => {
    const songId = song.id ?? song.ID;
    return fetchWrapperRef.current.post(`${config.apiBaseUrl}/bands/${bandid}/songs/${songId}/find-match`, query);
  };

  // Confirms the match: the catalogue song replaces the band's own under a
  // new id, and the modal closes. Rejects with the request's error.
  const handleConfirmMatch = async (song, match) => {
    const songId = song.id ?? song.ID;
    const response = await fetchWrapperRef.current.post(`${config.apiBaseUrl}/bands/${bandid}/songs/${songId}/match`, match);
    trackSongEvent('song_edited', songId, { edit: 'match' });
    // The band's own song info is dropped for the catalogue's.
    const {
      original_album: _album, release_date: _date, song_writer: _writer, genres: _genres, explicit: _explicit, ...bandFields
    } = song;
    const matched = songFrom(response, bandFields) ?? { ...bandFields, ...match, is_manual: false };
    replaceSong(songId, matched);
    closeModal();
  };

  const getMenuItems = (song) => {
    const songId = song.id;
    return [
      { label: t('common.actions.edit'), icon: FaEdit, action: () => handleEdit(song) },
      { label: t('common.actions.delete'), icon: FaTrash, action: () => handleDelete(song) },
    ];
  };

  const handleRowSelect = (songId) => {
    setSelectedSongIds(prev => {
      const next = new Set(prev);
      if (next.has(songId)) next.delete(songId);
      else next.add(songId);
      return next;
    });
  };

  // Selection means visible selected songs (ENG-507): a filter or search
  // change prunes the selection to what is still shown, so a bulk action
  // never touches a song the performer can't see.
  const visibleIdsRef = useRef(new Set());
  visibleIdsRef.current = new Set(sortedSongs.map(s => s.id));
  useEffect(() => {
    setSelectedSongIds(prev => {
      const pruned = new Set([...prev].filter(id => visibleIdsRef.current.has(id)));
      return pruned.size === prev.size ? prev : pruned;
    });
  }, [searchTerm, selectedTagFilter, visibilityFilter]);

  const isAllSelected = sortedSongs.length > 0 && sortedSongs.every(s => selectedSongIds.has(s.id));
  const isSomeSelected = !isAllSelected && sortedSongs.some(s => selectedSongIds.has(s.id));

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedSongIds(new Set());
    } else {
      setSelectedSongIds(new Set(sortedSongs.map(s => s.id)));
    }
  };

  const handleBulkSetActive = async (active) => {
    if (isBulkUpdating) return;
    setIsBulkUpdating(true);
    setListError(null);
    try {
      const ids = [...selectedSongIds].filter(id => visibleIdsRef.current.has(id));
      const results = await Promise.allSettled(
        ids.map(songId =>
          fetchWrapperRef.current.put(`${config.apiBaseUrl}/bands/${bandid}/songs/${songId}`, { active })
        )
      );
      const updatedIds = new Set(ids.filter((_, i) => results[i].status === 'fulfilled'));
      const failedIds = new Set(ids.filter((_, i) => results[i].status === 'rejected'));

      setSongs(prev => prev.map(s => updatedIds.has(s.id) ? { ...s, active } : s));
      setSelectedSongIds(failedIds);

      if (failedIds.size > 0) {
        console.error(`Failed to update ${failedIds.size} song(s).`);
        setListError({ key: 'songList.errors.bulkFailed', params: { count: failedIds.size, total: ids.length } });
      }
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const getCellClass = (songId) => {
    if (highlightedRow === songId) return styles.gridCellHighlight;
    if (selectedSongIds.has(songId)) return styles.gridCellSelected;
    return styles.gridCell;
  };

  const renderTagChips = (tags) => {
    if (!tags || tags.length === 0) return null;
    return (
      <div className={styles.tagChips}>
        {tags.map((tag) => {
          const imageUrl = tag.image_url || tag.ImageURL;
          return (
            <span
              key={tag.id || tag.ID}
              className={styles.tagChip}
              style={{
                backgroundColor: tag.background_color || tag.BackgroundColor || 'rgba(246, 205, 196, 0.85)',
                color: tag.text_color || tag.TextColor || '#830C0F',
              }}
            >
              {imageUrl && (
                <img
                  src={resolveMediaUrl(imageUrl)}
                  alt=""
                  className={styles.tagIconSmall}
                />
              )}
              {tag.word || tag.Word}
            </span>
          );
        })}
      </div>
    );
  };

  return (
    <div className={styles.page}>
      <div className={styles.listHeader}>
        <h2 className={styles.listTitle}>{t('songList.header.title')}</h2>
        <span className={styles.songCountBadge} data-testid="song-count">{t('songList.header.songCount', { count: songs.length, formattedCount: format.number(songs.length) })}</span>
        <div className={styles.listHeaderSpacer} />
        <Link to={`/performers/${bandid}/add-song`} className={styles.addSongButton}>
          <FaPlus aria-hidden="true" /> {t('songList.header.addSong')}
        </Link>
      </div>

      <div className={styles.topBar}>
        <SongFilter
          bandTags={bandTags}
          selectedTagFilter={selectedTagFilter}
          onFilterChange={setSelectedTagFilter}
          visibilityFilter={visibilityFilter}
          onVisibilityFilterChange={setVisibilityFilter}
          searchTerm={searchTerm}
          onSearchChange={performSearch}
        />
      </div>

      {listError && (
        <p className={styles.listError} role="alert" data-testid="song-list-error">{t(listError.key, listError.params)}</p>
      )}

      {/* Selection actions: sits below the filter/tag row, directly above the song grid header */}
      {selectedSongIds.size > 0 && (
        <div className={styles.selectionBar} role="region" aria-label={t('songList.selection.regionLabel')}>
          {/* Count label matches the ENG-307 reference ("3 selected"); the buttons reference it via
              aria-describedby so their accessible name stays equal to their visible label */}
          <span id={selectionCountId} className={styles.selectionBarLabel}>
            {t('songList.selection.selectedCount', { count: selectedSongIds.size })}
          </span>
          <div className={styles.selectionBarActions}>
            <button
              type="button"
              className={styles.selectionBarAction}
              onClick={() => handleBulkSetActive(true)}
              disabled={isBulkUpdating}
              aria-describedby={selectionCountId}
            >
              {t('songList.selection.makePublic')}
            </button>
            <button
              type="button"
              className={styles.selectionBarAction}
              onClick={() => handleBulkSetActive(false)}
              disabled={isBulkUpdating}
              aria-describedby={selectionCountId}
            >
              {t('songList.selection.makePrivate')}
            </button>
          </div>
        </div>
      )}

      <div className={styles.tableContainer}>
        {!songsFetched ? (
          <p className={styles.emptyState}>{t('songList.table.loading')}</p>
        ) : songs.length === 0 ? (
          <p className={styles.emptyState} data-testid="song-list-empty">{t('songList.table.empty')}</p>
        ) : sortedSongs.length === 0 ? (
          <p className={styles.emptyState} data-testid="song-list-no-match">{t('songList.table.noMatch')}</p>
        ) : (
          <div className={styles.grid} role="table" aria-label={t('songList.table.label')} data-testid="song-grid">
            <div role="rowgroup">
              <div role="row">
                <div className={`${styles.gridHeader} ${styles.checkboxHeader}`} role="columnheader">
                  <input
                    type="checkbox"
                    className={styles.rowCheckbox}
                    checked={isAllSelected}
                    ref={el => { if (el) el.indeterminate = isSomeSelected; }}
                    onChange={handleSelectAll}
                    aria-label={t('songList.table.selectAll')}
                  />
                </div>
                <button
                  className={`${styles.gridHeader} ${styles.sortable}`}
                  role="columnheader"
                  onClick={() => handleSort('title')}
                  aria-sort={sortConfig.key === 'title' ? (sortConfig.direction === 'ascending' ? 'ascending' : 'descending') : 'none'}
                >
                  {t('songList.table.title')} {getSortIcon('title')}
                </button>
                <button
                  className={`${styles.gridHeader} ${styles.sortable} ${styles.artistHeader}`}
                  role="columnheader"
                  onClick={() => handleSort('artist')}
                  aria-sort={sortConfig.key === 'artist' ? (sortConfig.direction === 'ascending' ? 'ascending' : 'descending') : 'none'}
                >
                  {t('songList.table.artist')} {getSortIcon('artist')}
                </button>
                <div
                  className={`${styles.gridHeader} ${styles.tagsHeader}`}
                  role="columnheader"
                >
                  {t('songList.table.tags')}
                </div>
                <div
                  className={`${styles.gridHeader} ${styles.activeHeader}`}
                  role="columnheader"
                >
                  {t('songList.table.public')} <FaInfoCircle className={styles.infoIcon} aria-label={t('songList.table.publicInfo')} />
                </div>
                <div className={`${styles.gridHeader} ${styles.actionHeader}`} role="columnheader"></div>
              </div>
            </div>
            <div role="rowgroup">
              {sortedSongs.map((song) => {
                const songId = song.id;
                const isSelected = selectedSongIds.has(songId);
                const isInactive = !song.active;

                return (
                  <div key={songId} role="row">
                    <div
                      className={`${getCellClass(songId)} ${styles.checkboxCell}`}
                      role="cell"
                    >
                      <input
                        type="checkbox"
                        className={styles.rowCheckbox}
                        checked={isSelected}
                        onChange={() => handleRowSelect(songId)}
                        aria-label={t('songList.table.selectSong', { title: song.title })}
                      />
                    </div>
                    <div className={getCellClass(songId)} role="cell">
                      <div className={styles.titleCellContent}>
                        <span className={`${styles.cellText} ${isInactive ? styles.cellTextInactive : ''}`}>{song.title}</span>
                      </div>
                    </div>
                    <div className={`${getCellClass(songId)} ${styles.artistCell}`} role="cell">
                      <span className={`${styles.cellText} ${isInactive ? styles.cellTextInactive : ''}`}>{song.artist}</span>
                    </div>
                    <div className={`${getCellClass(songId)} ${styles.requestedCell}`} role="cell">
                      {renderTagChips(song.tags)}
                    </div>
                    <div className={`${getCellClass(songId)} ${styles.activeCell}`} role="cell">
                      <Switch
                        isOn={song.active}
                        handleToggle={() => toggleActive(songId, song.active)}
                        switchId={`switch-${songId}`}
                      />
                    </div>
                    <div className={`${styles.actionCell}${highlightedRow === songId ? ` ${styles.actionCellHighlight}` : isSelected ? ` ${styles.actionCellSelected}` : ''}`} role="cell">
                      <ActionMenuButton
                        itemId={songId}
                        isOpen={openMenuId === songId}
                        onToggle={handleMenuToggle}
                        menuItems={getMenuItems(song)}
                        triggerLabel={t('songList.actions.menu')}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <EditSongModal
        song={currentSong}
        isOpen={editModalOpen}
        onClose={closeModal}
        onSave={handleSave}
        onConvertToManual={handleConvertToManual}
        onFindMatch={handleFindMatch}
        onConfirmMatch={handleConfirmMatch}
      />

      <ConfirmationModal
        isOpen={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        confirmTestId="song-delete-confirm"
        title={t('songList.deleteModal.title')}
        message={pendingDelete ? t('songList.deleteModal.message', { title: pendingDelete.title }) : ''}
        confirmText={t('common.actions.delete')}
        cancelText={t('common.actions.cancel')}
        isDangerous={true}
        error={deleteError}
        isBusy={isDeleting}
      />
    </div>
  );
};

export default PerformerSongList;
